import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Paperclip, Plus, X, Download, Trash2, Play, Loader2 } from "lucide-react";
import api, { fileUrl, getToken, formatErr } from "../../lib/apiClient";

/**
 * Part 20 (Feb 2026) — Sidebar Attachments widget on JobDetail.
 * Replaces the row of stub cards (Checklists / Tasks / Fields / …) with a
 * single real widget that:
 *   - lists the job's public photos/videos (job_photos where is_private=false)
 *   - lets the user upload additional images or videos
 *   - opens each attachment in a modal viewer on click
 *   - supports multi-select + batch download as a ZIP (mirrors Item 6 expenses)
 *
 * Data source: `job.photos` array already loaded by GET /api/jobs/{id}
 * (server projection: photos = job_photos where is_deleted=false and is_private!=true).
 */
export function SidebarAttachments({ job, reload, setDlg }) {
  const [selected, setSelected] = useState(new Set());
  const [viewer, setViewer] = useState(null); // { id, url, type, filename }
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [err, setErr] = useState("");
  const fileInputRef = useRef(null);

  const attachments = job?.photos || [];

  const isVideo = (a) => (a.content_type || "").startsWith("video/");
  const attUrl = (a) => fileUrl(`/job-photos/${a.id}/download`);

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected((prev) => prev.size === attachments.length ? new Set() : new Set(attachments.map((a) => a.id)));
  };
  const allSelected = attachments.length > 0 && selected.size === attachments.length;

  const openViewer = (a) => {
    setViewer({
      id: a.id,
      url: attUrl(a),
      type: isVideo(a) ? "video" : "image",
      filename: a.filename || "attachment",
    });
  };

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    setErr("");
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/jobs/${job.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      await reload();
    } catch (ex) {
      setErr(formatErr(ex));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const downloadSelected = async () => {
    if (selected.size === 0) return;
    setDownloading(true);
    setErr("");
    try {
      const token = getToken();
      const res = await fetch(fileUrl(`/jobs/${job.id}/attachments/batch-download`), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ photo_ids: [...selected] }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = `job-${job.number || job.id}-attachments.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(href), 30000);
    } catch (ex) {
      setErr(`Download failed: ${ex.message || ex}`);
    } finally {
      setDownloading(false);
    }
  };

  const deleteOne = (att) => {
    // Part 21 (Feb 2026): use ConfirmDialog instead of window.confirm
    // (window.confirm doesn't render in the preview iframe environment and
    // matches the app's uniform destructive-action pattern).
    setDlg?.({
      title: "Delete attachment?",
      body: `"${att.filename || "This file"}" will be permanently removed from this job. This action cannot be undone.`,
      confirmLabel: "Delete",
      danger: true,
      onConfirm: async () => {
        try {
          await api.delete(`/job-photos/${att.id}`);
          setSelected((prev) => { const n = new Set(prev); n.delete(att.id); return n; });
          await reload();
        } catch (ex) { setErr(formatErr(ex)); }
      },
    });
  };

  const deleteSelected = () => {
    if (selected.size === 0) return;
    const count = selected.size;
    setDlg?.({
      title: `Delete ${count} attachment${count === 1 ? "" : "s"}?`,
      body: `${count} selected file${count === 1 ? "" : "s"} will be permanently removed from this job. This action cannot be undone.`,
      confirmLabel: `Delete ${count}`,
      danger: true,
      onConfirm: async () => {
        setDownloading(true); // reuse busy flag for buttons
        try {
          await api.post(`/jobs/${job.id}/attachments/batch-delete`, { photo_ids: [...selected] });
          setSelected(new Set());
          await reload();
        } catch (ex) { setErr(formatErr(ex)); }
        finally { setDownloading(false); }
      },
    });
  };

  return (
    <div
      data-testid="sidebar-attachments"
      className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md"
    >
      <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Paperclip size={15} className="text-[#0F4C81] shrink-0" />
          <h4 className="font-display font-semibold text-slate-900 dark:text-slate-100 text-sm truncate">
            Attachments {attachments.length > 0 && <span className="text-slate-500 dark:text-slate-400 font-normal">({attachments.length})</span>}
          </h4>
        </div>
        <label
          data-testid="sidebar-attachments-add-btn"
          title="Upload photo or video"
          className="p-1 text-[#0F4C81] hover:bg-[#0F4C81]/5 rounded cursor-pointer"
        >
          {uploading ? <Loader2 size={16} className="animate-spin"/> : <Plus size={16} />}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            className="hidden"
            onChange={upload}
            disabled={uploading}
            data-testid="sidebar-attachments-file-input"
          />
        </label>
      </div>

      {err && (
        <div className="mx-4 mt-3 px-3 py-2 text-xs bg-rose-50 text-rose-800 border border-rose-200 rounded" data-testid="sidebar-attachments-error">
          {err}
        </div>
      )}

      <div className="p-4">
        {attachments.length === 0 ? (
          <div className="text-center py-6">
            <Paperclip size={28} className="mx-auto text-slate-300 dark:text-slate-700 mb-2" />
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">No attachments yet.</p>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="text-xs font-medium text-[#0F4C81] hover:underline"
              data-testid="sidebar-attachments-empty-add"
            >
              + Add a photo or video
            </button>
          </div>
        ) : (
          <>
            {/* multi-select toolbar */}
            <div className="flex items-center justify-between mb-2 text-xs">
              <label className="flex items-center gap-1.5 text-slate-600 dark:text-slate-400 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  data-testid="sidebar-attachments-select-all"
                  className="rounded"
                />
                {selected.size > 0 ? `${selected.size} selected` : "Select all"}
              </label>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={deleteSelected}
                  disabled={selected.size === 0 || downloading}
                  data-testid="sidebar-attachments-delete-selected"
                  className="px-2.5 py-1 text-xs font-medium text-rose-700 border border-rose-200 bg-white hover:bg-rose-50 rounded disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
                  title={selected.size > 0 ? `Delete ${selected.size} selected` : "Select attachments to delete"}
                >
                  <Trash2 size={12}/>
                  Delete
                </button>
                <button
                  onClick={downloadSelected}
                  disabled={selected.size === 0 || downloading}
                  data-testid="sidebar-attachments-download-selected"
                  className="px-2.5 py-1 text-xs font-medium text-white bg-[#0F4C81] hover:bg-[#0A365C] rounded disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
                >
                  {downloading ? <Loader2 size={12} className="animate-spin"/> : <Download size={12}/>}
                  Download
                </button>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {attachments.map((a) => {
                const checked = selected.has(a.id);
                const video = isVideo(a);
                return (
                  <div
                    key={a.id}
                    className={`relative group aspect-square border rounded overflow-hidden bg-slate-50 dark:bg-slate-800 ${
                      checked ? "border-[#0F4C81] ring-2 ring-[#0F4C81]/30" : "border-slate-200 dark:border-slate-700"
                    }`}
                    data-testid={`sidebar-attachment-tile-${a.id}`}
                  >
                    {/* Selection checkbox */}
                    <label className="absolute top-1 left-1 z-10 bg-white/95 rounded p-0.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleOne(a.id)}
                        data-testid={`sidebar-attachment-check-${a.id}`}
                        className="w-3.5 h-3.5"
                      />
                    </label>
                    {/* Delete on hover — now uses ConfirmDialog */}
                    <button
                      onClick={() => deleteOne(a)}
                      title="Delete"
                      data-testid={`sidebar-attachment-delete-${a.id}`}
                      className="absolute top-1 right-1 z-10 p-1 bg-white/95 text-rose-600 rounded opacity-0 group-hover:opacity-100 hover:bg-rose-50"
                    >
                      <Trash2 size={11}/>
                    </button>
                    {/* Preview — click to open viewer */}
                    <button
                      onClick={() => openViewer(a)}
                      data-testid={`sidebar-attachment-open-${a.id}`}
                      className="w-full h-full flex items-center justify-center"
                      title={a.filename || "View"}
                    >
                      {video ? (
                        <div className="relative w-full h-full">
                          <video src={attUrl(a)} className="w-full h-full object-cover" preload="metadata" />
                          <span className="absolute inset-0 flex items-center justify-center bg-black/30">
                            <Play size={22} className="text-white drop-shadow" fill="currentColor"/>
                          </span>
                        </div>
                      ) : (
                        <img src={attUrl(a)} alt={a.filename || ""} loading="lazy" className="w-full h-full object-cover" />
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {viewer && (
        <AttachmentViewer viewer={viewer} onClose={() => setViewer(null)} />
      )}
    </div>
  );
}

function AttachmentViewer({ viewer, onClose }) {
  // Iter 30 fix (Feb 2026): render to document.body via portal so the fixed
  // overlay isn't trapped inside the sidebar's stacking context. Previously
  // the top-nav header (z-40) intercepted clicks at the top-right where the
  // close (X) button lives, causing the X to appear inoperative.
  const handleClose = useCallback((e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    onClose();
  }, [onClose]);

  useEffect(() => {
    const onKey = (ev) => { if (ev.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-black/85 flex items-center justify-center p-6"
      onClick={handleClose}
      data-testid="attachment-viewer-backdrop"
    >
      <button
        onClick={handleClose}
        onMouseDown={(e) => e.stopPropagation()}
        data-testid="attachment-viewer-close"
        className="absolute top-4 right-4 text-white p-2 hover:bg-white/10 rounded z-[10000] bg-black/40"
        aria-label="Close viewer"
      >
        <X size={22}/>
      </button>
      <div
        className="max-w-6xl max-h-[92vh] w-full h-full flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        {viewer.type === "video" ? (
          <video
            src={viewer.url}
            controls
            autoPlay
            className="max-w-full max-h-full rounded shadow-2xl"
            data-testid="attachment-viewer-video"
          />
        ) : (
          <img
            src={viewer.url}
            alt={viewer.filename}
            className="max-w-full max-h-full object-contain rounded shadow-2xl"
            data-testid="attachment-viewer-image"
          />
        )}
      </div>
    </div>,
    document.body
  );
}
